import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import icon1Url from '@resources/img/pages/products/snapback-with-printed-patch/icon-1.svg?url';
import icon2Url from '@resources/img/pages/products/snapback-with-printed-patch/icon-2.svg?url';
import icon3Url from '@resources/img/pages/products/snapback-with-printed-patch/icon-3.svg?url';
import icon4Url from '@resources/img/pages/products/snapback-with-printed-patch/icon-4.svg?url';
import icon5Url from '@resources/img/pages/products/snapback-with-printed-patch/icon-5.svg?url';
import measurementsUrl from '@resources/img/pages/products/snapback-with-printed-patch/measurements.svg?url';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';
import SizeRangeDisplay from '../../../Common/Measurements/SizeRangeDisplay';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionPricing from '../../_Sections/SectionPricing';
import SectionSizes from '../../_Sections/SectionSizes';
import PrintedPatchMeasurementDiagram from '../Patches/PrintedPatchMeasurementDiagram';

const PRODUCT_ID = 139;
const VIEW_PRODUCT_ID = 170;

const bannerImages = {
  en_CA: new URL('@resources/img/pages/products/snapback-with-printed-patch/banner-1-en_CA.png', import.meta.url).href,
  fr_CA: new URL('@resources/img/pages/products/snapback-with-printed-patch/banner-1-fr_CA.png', import.meta.url).href,
};

const galleryImages = [
  new URL('@resources/img/pages/products/snapback-with-printed-patch/gallery_0.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/snapback-with-printed-patch/gallery_1.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/snapback-with-printed-patch/gallery_2.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/snapback-with-printed-patch/gallery_3.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/snapback-with-printed-patch/gallery_4.jpg', import.meta.url).href,
];

const colors = [
  {
    name: 'White',
    image: new URL('@resources/img/pages/products/snapbacks/WHITE.png', import.meta.url).href,
  },
  {
    name: 'Black',
    image: new URL('@resources/img/pages/products/snapbacks/BLACK.png', import.meta.url).href,
  },
  {
    name: 'Heather Grey',
    image: new URL('@resources/img/pages/products/snapbacks/HEATHER-GREY.png', import.meta.url).href,
  },
  {
    name: 'Dark Heather',
    image: new URL('@resources/img/pages/products/snapbacks/DARK-HEATHER.png', import.meta.url).href,
  },
  {
    name: 'Navy',
    image: new URL('@resources/img/pages/products/snapbacks/NAVY.png', import.meta.url).href,
  },
  {
    name: 'Black / Silver',
    image: new URL('@resources/img/pages/products/snapbacks/BLACK_SILVER.png', import.meta.url).href,
  },
  {
    name: 'Navy / Red',
    image: new URL('@resources/img/pages/products/snapbacks/NAVY_RED.png', import.meta.url).href,
  },
  {
    name: 'Royal',
    image: new URL('@resources/img/pages/products/snapbacks/ROYAL.png', import.meta.url).href,
  },
  {
    name: 'Maroon',
    image: new URL('@resources/img/pages/products/snapbacks/MAROON.png', import.meta.url).href,
  },
  {
    name: 'Heather / Black',
    image: new URL('@resources/img/pages/products/snapbacks/HEATHER_BLACK.png', import.meta.url).href,
  },
];

const uses = [
  {
    title: 'Event Merch',
    text: 'Create custom hats for festivals, conventions, launches, weddings, and other special events!',
  },
  {
    title: 'Team & Corporate Gear',
    text: 'Bring your team, business, or organization together with custom hats featuring your logo or artwork.',
  },
  {
    title: 'Limited-Edition Drops',
    text: 'Create exclusive custom hats for new collections, collaborations, seasonal releases, or special designs.',
  },
];

export default function SnapbackWithPrintedPatchPage() {
  const [t] = useTranslation('page/products/snapback-with-printed-patch');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const bannerImage = locale === 'fr_CA' ? bannerImages.fr_CA : bannerImages.en_CA;

  if (!productData) return null;

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom Snapback with Printed Patch | Products')}
      description={t('Create a custom snapback hat with a printed PVC patch featuring your artwork, logo, or design. ✔ No minimums ✔ Made to order in Canada')}
    />

    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#abebb9"
      bgStyle={{
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '50%',
      }}
      tag={t('Placement print')}
      title={t('Snapback with Printed Patch')}
      text={t('Make a statement with a custom snapback and printed PVC patch! Pair a structured, high-profile snapback with a custom patch featuring your artwork or logo. Choose your hat colour and customize it with one of four patch shapes, each available in two sizes—perfect for standout merch, branded gear, or a one-of-a-kind accessory.')}
      createUrl={productData.get('url_slug')}
      img={bannerImage}
      alt={t('Picture of Snapback with Printed Patch')}
      details={[
        {
          img: icon1Url,
          alt: t('PVC patch icon'),
          text: t('Custom full-colour, vibrant printed patch made from waterproof PVC'),
        },
        {
          img: icon4Url,
          alt: t('Snapback cap icon'),
          text: t('Premium Flat Bill Snapback Cap from YP Classics, made from 80% acrylic and 20% wool'),
        },
        {
          img: icon3Url,
          alt: t('Structured cap icon'),
          text: t('Structured, six-panel and flat bill with green undervisor'),
        },
        {
          img: icon2Url,
          alt: t('Sublimation printing icon'),
          text: t('Patch is printed with vibrant and permanent sublimation inks'),
        },
        {
          img: icon5Url,
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />

    <SectionGallery images={galleryImages} />

    <SectionPricing productId={PRODUCT_ID} />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Snapback with Printed Patch Sizing')}
      text={t('Snapbacks are adjustable, with a structured six-panel, high-profile fit and green undervisor. Patches are available in 4 shapes and 2 sizes per shape. Please allow for some variance in the placement of your design within an order. Patches are handled individually by staff, so there may be some differences in image placement.')}
    >
      <Stack spacing={8} sx={{ mt: 6 }}>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h4" component="h2" color="common.white">
            {t('Snapback Sizing')}
          </Typography>
          <Box
            component="svg"
            viewBox="0 0 1000 293.936"
            role="img"
            aria-label={t('Snapback size chart')}
            sx={{
              display: 'block',
              height: 'auto',
              maxWidth: 800,
              width: '100%',

              '& .snapback-measurement-label': {
                fontSize: {
                  xs: 32,
                  sm: 26,
                  md: 21,
                },
              },
            }}
          >
            <image
              aria-hidden
              href={measurementsUrl}
              height="293.936"
              width="845"
              x="77.5"
            />
            <text
              className="snapback-measurement-label"
              fill="#fff"
              fontFamily="Poppins, sans-serif"
              fontWeight="500"
              textAnchor="end"
              x="327.5"
              y="115.106"
            >
              <SizeDisplay sizes={[3.75]} sourceUnit="in" displayType="physical" />
            </text>
            <text
              className="snapback-measurement-label"
              fill="#fff"
              fontFamily="Poppins, sans-serif"
              fontWeight="500"
              textAnchor="start"
              x="790"
              y="206.106"
            >
              <SizeRangeDisplay
                minSizes={[6.625]}
                maxSizes={[7.375]}
                sourceUnit="in"
                displayType="physical"
                separator=" – "
              />
            </text>
          </Box>
        </Stack>

        <Stack spacing={3} alignItems="center">
          <Typography variant="h4" component="h2" color="common.white">
            {t('Printed Patch Shapes and Sizes')}
          </Typography>
          <PrintedPatchMeasurementDiagram ariaLabel={t('Printed Patch shapes and sizes')} />
        </Stack>
      </Stack>
    </SectionSizes>

    <Box component="section" sx={{
      backgroundColor: '#333',
      py: {
        xs: 8,
        md: 12,
      },
    }}>
      <Container maxWidth="lg">
        <Stack spacing={6} alignItems="center">
          <Typography variant="h1" component="h2" color="common.white" textAlign="center">
            {t('Hat colours')}
          </Typography>
          <Box sx={{
            display: 'grid',
            gap: {
              xs: 3,
              md: 4,
            },
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            width: '100%',
          }}>
            {colors.map(color => <Stack key={color.name} spacing={1} alignItems="center">
              <img
                src={color.image}
                alt={t(color.name)}
                loading="lazy"
                style={{
                  height: 'auto',
                  maxWidth: 150,
                  width: '100%',
                }}
              />
              <Typography variant="h5" component="h3" color="common.white" textAlign="center">
                {t(color.name)}
              </Typography>
            </Stack>)}
          </Box>
        </Stack>
      </Container>
    </Box>

    {/*<Box component="section" sx={{
      backgroundColor: '#f0f0f0',
      py: {
        xs: 8,
        md: 12,
      },
    }}>
      <Container maxWidth="lg">
        <Stack spacing={6} alignItems="center">
          <Typography variant="h1" component="h2" color="secondary.main" textAlign="center">
            {t('Ways to use it')}
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {uses.map(use => <Grid key={use.title} item xs={12} sm={6} md={4}>
              <Stack spacing={2} textAlign="center">
                <Typography variant="h4" component="h3" color="primary.main">
                  {t(use.title)}
                </Typography>
                <Typography variant="body1">
                  {t(use.text)}
                </Typography>
              </Stack>
            </Grid>)}
          </Grid>
        </Stack>
      </Container>
    </Box>*/}

    <SectionBranding
      title={t('Brand your Snapback with Printed Patch')}
      text={t('Branding enhances your products. Add custom artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your custom snapback with these great products to create your own unique style.')}
      productSlugs={['beanies-adult', 'premium-t-shirt', 'stainless-steel-water-bottle']}
    />
  </div>;
}
